import { unwrap, type MeOut, type WorkspaceOut, type WorkspaceSummary } from "@sagot/api-client";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { createContext, useContext, type ReactNode } from "react";
import { Outlet, useParams } from "react-router";
import { setActiveWorkspaceId } from "../app/api";
import { queryKeys } from "../lib/query-keys";
import { NoWorkspacePage } from "../pages/forbidden-page";
import { PageSkeleton } from "./guards";
import { writeLastWorkspace } from "./last-workspace";
import { useApi, useSession } from "./session-provider";
import { useMe } from "./use-me";

interface WorkspaceContextValue {
  summary: WorkspaceSummary;
  me: MeOut;
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

/** Impersonation tokens are bound to one tenant (`ws` claim); other slugs are not reachable. */
function findWorkspace(me: MeOut, slug: string | undefined, impersonatedId: string | undefined): WorkspaceSummary | undefined {
  const active = me.workspaces.filter((w) => w.membership_status === "active" || impersonatedId === w.id);
  const match = active.find((w) => w.slug === slug);
  if (!impersonatedId) return match;
  return match?.id === impersonatedId ? match : undefined;
}

/** `/w/:ws/*`: resolves the slug to a membership and binds `X-Workspace-Id` for this tab. */
export function WorkspaceGate({ children }: { children?: ReactNode }) {
  const { ws } = useParams();
  const me = useMe();
  const session = useSession();
  if (me.isPending) return <PageSkeleton />;
  if (me.isError) return <NoWorkspacePage />;
  const summary = findWorkspace(me.data, ws, session.token?.claims?.ws);
  if (!summary) return <NoWorkspacePage />;
  setActiveWorkspaceId(summary.id);
  writeLastWorkspace(summary.slug);
  return <WorkspaceContext.Provider value={{ summary, me: me.data }}>{children ?? <Outlet />}</WorkspaceContext.Provider>;
}

/**
 * Workspace context for pages outside `/w/:ws` (onboarding runs at `/onboarding?ws=`), so feature
 * hooks that key their caches by `useWorkspaceId()` work there. Renders `fallback` until the
 * membership is known; the caller has already bound `X-Workspace-Id`.
 */
export function WorkspaceScope({ workspaceId, fallback = null, children }: { workspaceId: string; fallback?: ReactNode; children: ReactNode }) {
  const me = useMe();
  const summary = me.data?.workspaces.find((w) => w.id === workspaceId);
  if (!me.data || !summary) return fallback;
  return <WorkspaceContext.Provider value={{ summary, me: me.data }}>{children}</WorkspaceContext.Provider>;
}

export function useWorkspaceContext(): WorkspaceContextValue {
  const value = useContext(WorkspaceContext);
  if (!value) throw new Error("WorkspaceGate is missing");
  return value;
}

export function useWorkspaceId(): string {
  return useWorkspaceContext().summary.id;
}

/** `GET /v1/workspace`: plan, industry and profile of the current workspace. */
export function useWorkspaceDetails(): UseQueryResult<WorkspaceOut> {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({ queryKey: queryKeys.workspace(wsId), queryFn: () => unwrap(api.GET("/v1/workspace")), staleTime: 60_000 });
}
